import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { DialogContent, TextField, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { setLoadingPanel } from "../../redux/features/loading";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";
import { formatDate } from "utils";
import { earningsCalulatorbyBrand } from "apis/earningsApi";

const getProductName = (product) => {
  if (product?.product) {
    return `${product.product.primaryName || ""} ${product.product.secondaryName || ""}`.trim();
  }
  return `${product?.primaryName || ""} ${product?.secondaryName || ""}`.trim();
};

const ProductsDialog = ({ open, onClose, scheme }) => (
  <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
    <AppBar sx={{ position: "relative" }}>
      <Toolbar>
        <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
        <Typography sx={{ ml: 5, flex: 1 }} variant="h6">
          {scheme.name}
        </Typography>
      </Toolbar>
    </AppBar>
    <DialogContent>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr className="divide-x divide-gray-200">
            <TableHeader heading="#" />
            <TableHeader heading="Product Name" />
            <TableHeader heading="Range" />
            <TableHeader heading="Period" />
            <TableHeader heading="Amount" />
            <TableHeader heading="Gift" />
            <TableHeader heading="Description" />
            <TableHeader heading="Remark" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {scheme.products?.map((product, i) => {
            const productName = getProductName(product);
            const slabs = product.slabs || [];

            if (slabs.length === 0) {
              return (
                <tr key={product.id || i} className="divide-x divide-gray-200 hover:bg-slate-100">
                  <TableElement value={i + 1} />
                  <TableElement value={productName} />
                  <TableElement value="-" />
                  <TableElement value="-" />
                  <TableElement value="-" />
                  <TableElement value="-" />
                  <TableElement value="-" />
                  <TableElement value="-" />
                </tr>
              );
            }

            return slabs.map((slab, slabIdx) => (
              <tr
                key={`${product.id || i}-${slab.id || slabIdx}`}
                className="divide-x divide-gray-200 hover:bg-slate-100"
              >
                {slabIdx === 0 && (
                  <>
                    <td rowSpan={slabs.length} className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200 align-top">
                      {i + 1}
                    </td>
                    <td rowSpan={slabs.length} className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200 align-top">
                      {productName}
                    </td>
                  </>
                )}
                <TableElement value={`${slab.rangeFrom || 0} - ${slab.rangeTo || 0}`} />
                <TableElement value={`${formatDate(slab.periodFrom)} - ${formatDate(slab.periodTo)}`} />
                <TableElement value={slab.amount || "-"} />
                <TableElement value={slab.gift || "-"} />
                <TableElement value={slab.description || "-"} />
                <TableElement value={slab.remark || "-"} />
              </tr>
            ));
          })}
        </tbody>
      </table>
    </DialogContent>
  </Dialog>
);

const EarningsDialog = ({ open, onClose, scheme }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [earningsData, setEarningsData] = useState(null);
  const [filterPayload, setFilterPayload] = useState({
    periodFrom: scheme.periodFrom || "",
    periodTo: scheme.periodTo || "",
    targetfromRetailer: "",
  });

  const handleClose = () => {
    setEarningsData(null);
    setFilterPayload({
      periodFrom: scheme.periodFrom || "",
      periodTo: scheme.periodTo || "",
      targetfromRetailer: "",
    });
    onClose();
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("en-IN").format(value);
  };

  const handleCalculate = async () => {
    if (!filterPayload.periodFrom || !filterPayload.periodTo) {
      toast.error("Please select both Period From and Period To dates");
      return;
    }

    try {
      dispatch(setLoadingPanel(true));
      const response = await earningsCalulatorbyBrand({
        schemeId: scheme.id,
        periodFrom: filterPayload.periodFrom,
        periodTo: filterPayload.periodTo,
        targetfromRetailer: filterPayload.targetfromRetailer || null,
      });
      setEarningsData(response.data);
      toast.success("Earnings calculated successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to calculate earnings");
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
      setEarningsData(null);
    } finally {
      dispatch(setLoadingPanel(false));
    }
  };

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={handleClose}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            Earnings Calculator - {scheme.name}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <div className="mb-6 p-4 bg-gray-50 rounded">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Period From"
                value={filterPayload.periodFrom}
                onChange={(e) => setFilterPayload((prev) => ({ ...prev, periodFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Period To"
                value={filterPayload.periodTo}
                onChange={(e) => setFilterPayload((prev) => ({ ...prev, periodTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Target from Retailer"
                value={filterPayload.targetfromRetailer}
                onChange={(e) => setFilterPayload((prev) => ({ ...prev, targetfromRetailer: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleCalculate}
              >
                Calculate Earnings
              </Button>
            </Grid>
          </Grid>
        </div>

        {earningsData && earningsData.data && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-green-600 font-medium uppercase">Total Earnings</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(earningsData.data.totalEarning)}
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-600 font-medium uppercase">Eligible Count</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatNumber(earningsData.data.eligbleCount)}
                </p>
              </div>
            </div>

            {/* Brands and Products Breakdown */}
            {earningsData.data.brands && earningsData.data.brands.length > 0 ? (
              earningsData.data.brands.map((brand, brandIdx) => (
                <div key={brandIdx} className="mb-6">
                  <div className="bg-gray-100 p-3 rounded-t-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800">
                      {brand.brandName} - Earnings: {formatCurrency(brand.totalEarning)} | Eligible: {brand.eligbleCount}
                    </h4>
                  </div>

                  {brand.products && brand.products.length > 0 && (
                    <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr className="divide-x divide-gray-200">
                            <TableHeader heading="#" />
                            <TableHeader heading="Product Name" />
                            <TableHeader heading="Eligible Count" />
                            <TableHeader heading="Total Earning" />
                            <TableHeader heading="Scheme Details" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {brand.products.map((product, prodIdx) => (
                            <tr key={prodIdx} className="divide-x divide-gray-200 hover:bg-slate-100">
                              <TableElement value={prodIdx + 1} />
                              <TableElement value={product.productName || "-"} />
                              <TableElement value={formatNumber(product.eligbleCount)} />
                              <TableElement value={formatCurrency(product.totalEarning)} />
                              <TableElement
                                type="element"
                                value={
                                  product.schemes && product.schemes.length > 0 ? (
                                    <div className="text-xs">
                                      {product.schemes.map((sch, schIdx) => (
                                        <div key={schIdx} className="mb-1 p-2 bg-gray-50 rounded">
                                          <div><strong>Scheme:</strong> {sch.schemeName}</div>
                                          <div><strong>Type:</strong> {sch.payoutType}</div>
                                          <div><strong>Target Achieved:</strong> {sch.targetAchieved}</div>
                                          <div><strong>Earning:</strong> {formatCurrency(sch.earning)}</div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : "-"
                                }
                              />
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">No brand earnings data available</p>
              </div>
            )}
          </>
        )}

        {!earningsData && (
          <div className="text-center py-12 text-gray-400">
            <svg
              className="mx-auto h-12 w-12 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No data to display</h3>
            <p className="mt-1 text-sm text-gray-500">
              Select a date range and click Calculate Earnings
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SchemeRow = ({ index, scheme, onEdit }) => {
  const [showProducts, setShowProducts] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);

  return (
    <>
      <ProductsDialog open={showProducts} onClose={() => setShowProducts(false)} scheme={scheme} />
      <EarningsDialog open={showEarnings} onClose={() => setShowEarnings(false)} scheme={scheme} />

      <tr className="divide-x divide-gray-200 hover:bg-slate-100">
        <TableElement value={index + 1} />
        <TableElement value={`${scheme.brand?.name} | ${scheme.name}`} />
        <TableElement
          type="element"
          value={
            scheme.imageUrl && scheme.imageUrl !== "-" && (
              <a href={scheme.imageUrl} target="_blank" rel="noreferrer">
                <img
                  src={scheme.imageUrl}
                  alt={scheme.name}
                  style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </a>
            )
          }
        />
        <TableElement type="element" value={formatDate(scheme.periodFrom)} />
        <TableElement type="element" value={formatDate(scheme.periodTo)} />
        <TableElement
          type="element"
          value={
            <div className="flex gap-2 flex-wrap justify-center">
              {scheme.products?.length > 0 && (
                <Button onClick={() => setShowProducts(true)} variant="text" size="small">
                  Products ({scheme.products.length})
                </Button>
              )}
              <Button onClick={() => onEdit && onEdit(scheme)} variant="text" size="small" color="primary">
                Edit
              </Button>
              <Button onClick={() => setShowEarnings(true)} variant="text" size="small" color="success">
                Earnings
              </Button>
            </div>
          }
        />
        <TableElement value={scheme.status || "-"} />
        <TableElement value={scheme.isActive} type="boolean" />
      </tr>
    </>
  );
};

export default SchemeRow;
